// Extracts the most useful description available from anything that can be thrown —
// native bridges sometimes throw non-Error values (strings, plain objects, or even
// undefined), where `error.message` alone would just say "undefined".
function describeError (error) {
    if (error == null) return 'threw with no error object (native binding gave no detail)';
    if (typeof error === 'string') return error;
    if (error.message) return error.message;
    if (error.code) return `error code ${error.code}`;
    try {
        const json = JSON.stringify(error);
        if (json && json !== '{}') return json;
    } catch {
        // fall through to String(error) below
    }
    return String(error);
}

// BrightSign's Node runtime exposes `window.require` for native `@brightsign/*` modules.
// This is the only place that touches `window.require` directly — every accessor goes
// through here so the same bundle keeps working unmodified on non-BrightSign devices.
export function loadNativeModule (name) {
    try {
        if (typeof window === 'undefined' || typeof window.require !== 'function') {
            return null;
        }
        return window.require(name);
    } catch {
        return null;
    }
}

// BrightSign's docs render method names inconsistently across firmware versions
// (e.g. getEdidIdentity vs getedididentity) — try each candidate name in order.
export function pickMethod (instance, names) {
    for (const name of names) {
        if (instance && typeof instance[name] === 'function') {
            return instance[name].bind(instance);
        }
    }
    return null;
}

// Same inconsistency shows up on plain data properties too (outputpowered vs
// outputPowered) — try each candidate key in order, same as pickMethod does for methods.
export function pickProperty (obj, names) {
    for (const name of names) {
        if (obj && obj[name] !== undefined) {
            return obj[name];
        }
    }
    return undefined;
}

// Valid port/output identifiers also vary by device model and are undocumented beyond
// example strings — try each candidate constructor argument until one doesn't throw.
export function constructWithFallback (NativeClass, candidates) {
    // Empty candidates would fall through to `throw null`, which reaches describeError()
    // as a misleading "no error object" message — surface the real cause instead.
    if (!candidates || candidates.length === 0) {
        throw new Error('constructWithFallback: no candidate identifiers provided');
    }
    let lastError = null;
    for (const candidate of candidates) {
        try {
            return { instance: new NativeClass(candidate), name: candidate };
        } catch (error) {
            lastError = error;
        }
    }
    throw lastError;
}

export { describeError };
